/**
 * ─────────────────────────────────────────────────────────────────────
 * FILE: src/pages/FileAnalysis.tsx
 * DESCRIPTION: File upload and analysis interface for media verification.
 *              Provides drag-and-drop upload, watermarking options, and
 *              AI assessment configuration with real-time queue management.
 *
 * FEATURES:
 * - Drag-and-drop file upload with progress tracking
 * - Visible and invisible watermarking options
 * - AI assessment provider selection (Mistral, Qwen, OpenAI)
 * - Custom assessment prompts
 * - File queue with status tracking
 * - Multi-file upload support
 *
 * @copyright 2025-2026 Anou. All rights reserved.
 * @author Anou
 * @version 1.0.0
 * @created 2025-01-15
 * @updated 2026-03-26 - Redesigned with watermarking options, AI assessment, and file queue
 * ─────────────────────────────────────────────────────────────────────
 */

import { useState, useCallback } from 'react';
import { 
  Upload, 
  FileImage, 
  FileVideo, 
  FileAudio, 
  CheckCircle, 
  AlertCircle, 
  Clock, 
  ChevronDown,
  UploadCloud,
  Info,
  X
} from 'lucide-react';

interface QueuedFile {
  id: string;
  name: string;
  size: string;
  progress: number;
  status: 'queued' | 'processing' | 'completed' | 'failed';
  type: 'video' | 'image' | 'audio';
  expanded?: boolean;
}

const FileAnalysis: React.FC = () => {
  const [isDragging, setIsDragging] = useState(false);
  const [fileQueue, setFileQueue] = useState<QueuedFile[]>([
    {
      id: '1',
      name: 'VeriMark_Intro_Video_01.mp4',
      size: '45MB',
      progress: 75,
      status: 'processing',
      type: 'video',
      expanded: false
    },
    {
      id: '2',
      name: 'Logo_Draft_v3.png',
      size: '1.2MB',
      progress: 100,
      status: 'completed',
      type: 'image',
      expanded: false
    },
    {
      id: '3',
      name: 'Interview_Clip_02.wav',
      size: '8.5MB',
      progress: 20,
      status: 'queued',
      type: 'audio',
      expanded: false
    }
  ]);

  // Watermarking options
  const [applyVisibleWatermark, setApplyVisibleWatermark] = useState(true);
  const [visibleWatermarkOpacity, setVisibleWatermarkOpacity] = useState(70);
  const [applyInvisibleWatermark, setApplyInvisibleWatermark] = useState(false);
  
  // AI Assessment options
  const [enableAIAssessment, setEnableAIAssessment] = useState(true);
  const [aiProvider, setAiProvider] = useState('mistral');
  const [customPrompt, setCustomPrompt] = useState('');

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      handleFiles(files);
    }
  }, []);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      handleFiles(files);
    }
  };

  const handleFiles = (files: FileList) => {
    Array.from(files).forEach(file => {
      const newFile: QueuedFile = {
        id: Math.random().toString(36).substr(2, 9),
        name: file.name,
        size: `${(file.size / (1024 * 1024)).toFixed(1)}MB`,
        progress: 0,
        status: 'queued',
        type: file.type.startsWith('video') ? 'video' : file.type.startsWith('audio') ? 'audio' : 'image',
        expanded: false
      };
      setFileQueue(prev => [...prev, newFile]);
      // Simulate upload progress
      simulateUpload(newFile.id);
    });
  };

  const simulateUpload = (fileId: string) => {
    // Move to processing
    setTimeout(() => {
      setFileQueue(prev => prev.map(f => 
        f.id === fileId ? { ...f, status: 'processing' } : f
      ));
      
      // Simulate progress
      let progress = 0;
      const interval = setInterval(() => {
        progress += Math.random() * 15;
        if (progress >= 100) {
          progress = 100;
          clearInterval(interval);
          setFileQueue(prev => prev.map(f => 
            f.id === fileId ? { ...f, progress: 100, status: 'completed' } : f
          ));
        } else {
          setFileQueue(prev => prev.map(f => 
            f.id === fileId ? { ...f, progress } : f
          ));
        }
      }, 500);
    }, 1000);
  };

  const toggleExpand = (fileId: string) => {
    setFileQueue(prev => prev.map(f => 
      f.id === fileId ? { ...f, expanded: !f.expanded } : f
    ));
  };

  const removeFile = (fileId: string) => {
    setFileQueue(prev => prev.filter(f => f.id !== fileId));
  };

  const getFileIcon = (type: string) => {
    switch (type) {
      case 'video': return <FileVideo className="w-5 h-5" />;
      case 'audio': return <FileAudio className="w-5 h-5" />;
      default: return <FileImage className="w-5 h-5" />;
    }
  };

  const getStatusBadge = (status: string) => {
    const badges = {
      queued: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900 dark:text-yellow-300',
      processing: 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300',
      completed: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900 dark:text-emerald-300',
      failed: 'bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300'
    };
    return badges[status as keyof typeof badges] || badges.queued;
  };

  return (
    <div className="p-8">
      {/* Breadcrumb */}
      <nav className="mb-6">
        <ol className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
          <li className="hover:text-gray-900 dark:hover:text-white cursor-pointer">Home</li>
          <li>/</li>
          <li className="hover:text-gray-900 dark:hover:text-white cursor-pointer">Upload</li>
          <li>/</li>
          <li className="font-medium text-gray-900 dark:text-white">New Analysis</li>
        </ol>
      </nav>

      {/* Page Title */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">File Upload</h1>
        <p className="text-gray-600 dark:text-gray-400">Upload media files for analysis and verification</p>
      </div>

      {/* Upload Zone */}
      <div
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        className={`border-2 border-dashed rounded-xl p-12 text-center transition-all mb-4 ${
          isDragging
            ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
            : 'border-blue-300 bg-blue-50/50 dark:bg-blue-900/10 dark:border-blue-700'
        }`}
        style={{ minHeight: '400px' }}
      >
        <div className="w-20 h-20 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
          <UploadCloud className="w-10 h-10 text-blue-600 dark:text-blue-400" />
        </div>
        <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
          Drag & drop media files or click to browse
        </h3>
        <p className="text-gray-600 dark:text-gray-400 mb-6">
          Supports multiple file formats up to 100MB each
        </p>
        <label className="inline-flex items-center gap-2 px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg cursor-pointer transition-colors">
          <Upload className="w-5 h-5" />
          Select Files
          <input
            type="file"
            className="hidden"
            accept="image/*,video/*,audio/*"
            multiple
            onChange={handleFileSelect}
          />
        </label>
      </div>

      {/* File Info */}
      <div className="flex items-center justify-between mb-8 text-sm">
        <div className="flex items-center gap-2 text-gray-600 dark:text-gray-400">
          <Info className="w-4 h-4" />
          <span>Supported formats: MP4, MOV, MKV, JPEG, PNG, MP3, WAV</span>
        </div>
        <div className="flex items-center gap-2 text-gray-600 dark:text-gray-400">
          <Info className="w-4 h-4" />
          <span>Max file size: Up to 100MB per file</span>
        </div>
      </div>

      {/* Options Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        {/* Watermarking Options */}
        <div className="bg-white dark:bg-gray-800 rounded-xl p-6 border border-gray-200 dark:border-gray-700 shadow-sm">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
            Watermarking Options
          </h3>
          <div className="space-y-4">
            {/* Visible Watermark */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <button
                  onClick={() => setApplyVisibleWatermark(!applyVisibleWatermark)}
                  className={`w-11 h-6 rounded-full transition-colors ${
                    applyVisibleWatermark ? 'bg-blue-600' : 'bg-gray-300 dark:bg-gray-600'
                  }`}
                >
                  <div className={`w-5 h-5 bg-white rounded-full shadow-md transform transition-transform ${
                    applyVisibleWatermark ? 'translate-x-5' : 'translate-x-1'
                  }`} />
                </button>
                <span className="text-sm font-medium text-gray-900 dark:text-white">
                  Apply Visible Watermark
                </span>
              </div>
            </div>
            {applyVisibleWatermark && (
              <div className="pl-14">
                <div className="flex items-center gap-3">
                  <span className="text-sm text-gray-600 dark:text-gray-400">Opacity</span>
                  <input
                    type="range"
                    min="0"
                    max="100"
                    value={visibleWatermarkOpacity}
                    onChange={(e) => setVisibleWatermarkOpacity(Number(e.target.value))}
                    className="flex-1 h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer"
                  />
                  <span className="text-sm text-gray-900 dark:text-white w-12">{visibleWatermarkOpacity}%</span>
                </div>
              </div>
            )}

            {/* Invisible Watermark */}
            <div className="flex items-center justify-between pt-2">
              <div className="flex items-center gap-3">
                <button
                  onClick={() => setApplyInvisibleWatermark(!applyInvisibleWatermark)}
                  className={`w-11 h-6 rounded-full transition-colors ${
                    applyInvisibleWatermark ? 'bg-blue-600' : 'bg-gray-300 dark:bg-gray-600'
                  }`}
                >
                  <div className={`w-5 h-5 bg-white rounded-full shadow-md transform transition-transform ${
                    applyInvisibleWatermark ? 'translate-x-5' : 'translate-x-1'
                  }`} />
                </button>
                <span className="text-sm font-medium text-gray-900 dark:text-white">
                  Apply Invisible Watermark
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* AI Assessment */}
        <div className="bg-white dark:bg-gray-800 rounded-xl p-6 border border-gray-200 dark:border-gray-700 shadow-sm">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
            AI Assessment
          </h3>
          <div className="space-y-4">
            {/* Enable AI Assessment */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <button
                  onClick={() => setEnableAIAssessment(!enableAIAssessment)}
                  className={`w-11 h-6 rounded-full transition-colors ${
                    enableAIAssessment ? 'bg-blue-600' : 'bg-gray-300 dark:bg-gray-600'
                  }`}
                >
                  <div className={`w-5 h-5 bg-white rounded-full shadow-md transform transition-transform ${
                    enableAIAssessment ? 'translate-x-5' : 'translate-x-1'
                  }`} />
                </button>
                <span className="text-sm font-medium text-gray-900 dark:text-white">
                  Enable AI Assessment
                </span>
              </div>
            </div>

            {enableAIAssessment && (
              <>
                {/* Provider Dropdown */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Provider
                  </label>
                  <select
                    value={aiProvider}
                    onChange={(e) => setAiProvider(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="mistral">Mistral</option>
                    <option value="qwen">Qwen</option>
                    <option value="openai">OpenAI</option>
                  </select>
                </div>

                {/* Custom Prompt */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Custom Assessment Prompt
                  </label>
                  <input
                    type="text"
                    value={customPrompt}
                    onChange={(e) => setCustomPrompt(e.target.value)}
                    placeholder="Enter custom prompt for AI analysis"
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* File Queue */}
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm">
        <div className="p-6 border-b border-gray-200 dark:border-gray-700">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
            File Queue
          </h3>
        </div>
        <div className="divide-y divide-gray-200 dark:divide-gray-700">
          {fileQueue.map((file) => (
            <div key={file.id} className="p-4">
              <div className="flex items-center gap-4">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                  file.type === 'video' ? 'bg-purple-100 text-purple-600 dark:bg-purple-900/30 dark:text-purple-400' :
                  file.type === 'audio' ? 'bg-pink-100 text-pink-600 dark:bg-pink-900/30 dark:text-pink-400' :
                  'bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400'
                }`}>
                  {getFileIcon(file.type)}
                </div>
                
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between mb-2">
                    <div>
                      <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                        {file.name}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">{file.size}</p>
                    </div>
                    
                    <div className="flex items-center gap-3">
                      <span className={`inline-flex px-3 py-1 text-xs font-medium rounded-full ${getStatusBadge(file.status)}`}>
                        {file.status.charAt(0).toUpperCase() + file.status.slice(1)}
                      </span>
                      <button
                        onClick={() => toggleExpand(file.id)}
                        className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                      >
                        <ChevronDown className={`w-5 h-5 transition-transform ${file.expanded ? 'rotate-180' : ''}`} />
                      </button>
                      <button
                        onClick={() => removeFile(file.id)}
                        className="p-1 text-gray-400 hover:text-red-600 dark:hover:text-red-400"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                  
                  {/* Progress Bar */}
                  <div className="relative">
                    <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2 overflow-hidden">
                      <div
                        className="h-full rounded-full bg-gradient-to-r from-blue-500 to-emerald-500 transition-all duration-500"
                        style={{ width: `${file.progress}%` }}
                      />
                    </div>
                    <span className="absolute right-0 -top-5 text-xs text-gray-600 dark:text-gray-400">
                      {Math.round(file.progress)}%
                    </span>
                  </div>
                </div>
              </div>

              {/* Expanded Details */}
              {file.expanded && (
                <div className="mt-4 ml-14 p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-gray-600 dark:text-gray-400">File Type:</span>
                      <span className="ml-2 font-medium text-gray-900 dark:text-white capitalize">{file.type}</span>
                    </div>
                    <div>
                      <span className="text-gray-600 dark:text-gray-400">Status:</span>
                      <span className="ml-2 font-medium text-gray-900 dark:text-white capitalize">{file.status}</span>
                    </div>
                    <div>
                      <span className="text-gray-600 dark:text-gray-400">Progress:</span>
                      <span className="ml-2 font-medium text-gray-900 dark:text-white">{Math.round(file.progress)}%</span>
                    </div>
                    <div>
                      <span className="text-gray-600 dark:text-gray-400">ID:</span>
                      <span className="ml-2 font-medium text-gray-900 dark:text-white font-mono">{file.id}</span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default FileAnalysis;
